// src/games/CrossWords/index.tsx
import { message } from 'antd';
import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import type { FinishResult } from '../core/session/useGameSession';
import type { PassatempoDetail } from './api';
import Base from './base';
import GameContextProvider from './context/GameContext';
import type { iState } from './context/interfaces';
import { isDirectCrossword, loadCrossword } from './load';
import getPossibleClue from './utils/getPossibleClue';
import initializeHousesStates from './utils/initializeHousesStates';
import setSubFocus from './utils/setSubFocus';

interface Loaded {
  state: iState;
  detail: PassatempoDetail;
  direct: boolean;
  size: number;
  sizeHeight: number;
  savedSeconds: number;
  finished: FinishResult | null;
}

function firstAndLast(keys: string[]): [number, number] {
  return [parseInt(keys[0], 10), parseInt(keys[keys.length - 1], 10)];
}

const CrossWordsScreen: React.FC<{ id: number }> = ({ id }) => {
  const navigate = useNavigate();
  const [loaded, setLoaded] = useState<Loaded | null>(null);

  useEffect(() => {
    let alive = true;
    loadCrossword(id)
      .then(({ detail, template, savedGame, savedSeconds, finished }) => {
        if (!alive) return;
        const [firstClueNumberHorizontal, lastClueNumberHorizontal] = firstAndLast(Object.keys(template.chaves.horizontal));
        const [firstClueNumberVertical, lastClueNumberVertical] = firstAndLast(Object.keys(template.chaves.vertical));
        const firstPossibleClue = getPossibleClue(savedGame.cache.matrix, template);
        const { houseStates, maxScore } = initializeHousesStates(template, savedGame);
        const subFocus = setSubFocus({
          selectedClue: firstPossibleClue.clueNumber,
          orientation: firstPossibleClue.orientation,
          focusedHouse: firstPossibleClue.focusedHouse,
          houseStates,
        });
        setLoaded({
          state: {
            GameTemplate: template,
            GameState: {
              currentClueNumber: firstPossibleClue.clueNumber,
              currentClueText: firstPossibleClue.clueText,
              orientation: firstPossibleClue.orientation,
              cluesInfos: {
                firstClueNumberHorizontal,
                lastClueNumberHorizontal,
                firstClueNumberVertical,
                lastClueNumberVertical,
              },
              focusedHouse: subFocus.focusedHouse,
              finished: false,
              completed: false,
              score: savedGame.cache.pontuacao,
              time: savedSeconds,
              savedGame,
              maxScore,
              scrollElementRef: undefined,
              houseStates: subFocus.houseStates,
              rowsCoords: [],
              keyboardUP: false,
            },
          },
          detail,
          direct: isDirectCrossword(detail),
          size: template.matrix[0]?.length ?? 0,
          sizeHeight: template.matrix.length,
          savedSeconds,
          finished,
        });
      })
      .catch(() => {
        if (!alive) return;
        message.error('Não foi possível abrir o jogo. Tente de novo.');
        navigate('/');
      });
    return () => {
      alive = false;
    };
  }, [id, navigate]);

  if (!loaded) {
    return <div style={{ width: '100%', height: '100%' }} />;
  }

  return (
    <GameContextProvider INITIAL_STATE={loaded.state}>
      <Base
        id={id}
        direct={loaded.direct}
        gameDetail={loaded.detail}
        size={loaded.size}
        sizeHeight={loaded.sizeHeight}
        savedSeconds={loaded.savedSeconds}
        alreadyFinished={loaded.finished}
      />
    </GameContextProvider>
  );
};

/**
 * Rota `/jogo/palavrascruzadas/:id`. O `key` remonta carga, contexto e sessão quando o id muda
 * com a rota montada (ex.: link do menu para outra cruzada): sem isso, a grade do jogo A seria
 * salva e verificada no jogo B. A tela recebe o id por prop, nunca do `useParams`.
 */
export const CrossWords: React.FC = () => {
  const id = Number(useParams().id);
  return <CrossWordsScreen key={id} id={id} />;
};
